/**
 * PT-#58 — Bridge para persistir los ejes de la vista Tendencias dentro del MISMO
 * saved filter que Publicaciones/Estadísticas (opción b = EXTENDER el modelo existente,
 * NO crear un modelo paralelo de "saved trends").
 *
 * Problema: los ejes propios de Tendencias (nivel de análisis, agrupación, métrica, tipo
 * de precio, acotado temporal, límite de tomas + dimensiones combinables) viven como state
 * LOCAL de `CategoryPriceTrendCard`, desacoplado del `SaveFilterModal` (que está en
 * Dashboard y solo ve los filtros de datos de Dashboard). Levantar esos ~15 states a
 * Dashboard sería un refactor grande e innecesario para este scope.
 *
 * Solución: un registro singleton a nivel de módulo. La card registra un snapshot getter
 * + un applier; Dashboard los invoca al guardar/cargar un filtro con scope 'multi'
 * (Tendencias). Retrocompatible: si no hay card montada o el filtro no trae ejes de
 * tendencias, todo sigue funcionando (undefined-safe).
 */

/** Snapshot serializable de los ejes de la vista Tendencias que se persisten en el saved filter. */
export interface TrendsAxesSnapshot {
  // Dimensiones combinables (definen QUÉ se grafica).
  sites?: string[];
  categories?: string[];
  brands?: string[];
  subcategories?: string[];
  segments?: string[];
  // Ejes de análisis propios de Tendencias.
  analysisLevel?: 'listings' | 'skus';
  groupBy?: 'category' | 'brand';
  metric?: 'average' | 'median';
  priceType?: 'current' | 'original';
  tomasLimit?: number;
  expandSkus?: boolean;
  // Acotado temporal (el eje "period" es compuesto).
  filterMode?: 'tomas' | 'period';
  periodPreset?: '15d' | '1m' | '3m' | 'custom';
  dateFrom?: string; // 'YYYY-MM-DD'
  dateTo?: string; // 'YYYY-MM-DD'
}

interface TrendsBridgeHandlers {
  /** Devuelve el estado vivo de los ejes de Tendencias para guardarlo en el filtro. */
  getSnapshot: () => TrendsAxesSnapshot;
  /** Aplica un snapshot (cargado de un saved filter) a la vista Tendencias y regenera. */
  applySnapshot: (snap: TrendsAxesSnapshot) => void;
}

let handlers: TrendsBridgeHandlers | null = null;

/** La card llama esto al montar; devuelve un desregistrador para el cleanup. */
export function registerTrendsBridge(h: TrendsBridgeHandlers): () => void {
  handlers = h;
  return () => {
    if (handlers === h) handlers = null;
  };
}

/**
 * Snapshot de los ejes de Tendencias, o null si la card no está montada.
 * Dashboard lo usa al construir el objeto del saved filter (solo en scope 'multi').
 */
export function getTrendsSnapshot(): TrendsAxesSnapshot | null {
  return handlers ? handlers.getSnapshot() : null;
}

/**
 * Aplica un snapshot a la vista Tendencias. No-op seguro si la card no está montada
 * (ej. se cargó el filtro fuera de la pestaña Tendencias — el próximo mount no lo aplica,
 * pero el objeto guardado se conserva intacto y no rompe nada).
 */
export function applyTrendsSnapshot(snap: TrendsAxesSnapshot | null | undefined): void {
  if (handlers && snap) handlers.applySnapshot(snap);
}
